import dayjs from "dayjs"
import { useMemo } from "react"
import { IDeliveryNotePlus } from "../../../models/DeliveryNote"
import { districtLabel } from "../../../utils/district"
import { computeSla, PendingPriority } from "../atoms"

// Heuristic thresholds — wire to BusinessGovernanceConfig later.
const VIP_AMOUNT_THRESHOLD = 10_000_000

export interface PendingRowMeta {
  priority: PendingPriority
  slaSeverity: "OK" | "WARNING" | "CRITICAL"
  totalSizeNumeric: number
  district: string
}

export interface PendingDerived {
  metaByDn: Map<string, PendingRowMeta>
  countsByDistrict: Map<string, number>
  vipUrgentCount: number
  slaRiskCount: number
  bigFreightCount: number
  customerPickupCount: number
  avgWaitHours: number
  // AI grouping preview (rough, mockup-style — district buckets)
  aiRouteGroups: { district: string; count: number; totalSize: number }[]
}

function parseSize(size?: number | string | null): number {
  if (size == null) return 0
  if (typeof size === "number") return size
  const m = /(\d+(?:\.\d+)?)/.exec(size)
  return m ? Number(m[1]) : 0
}

export function useDerivedPendingData(rows: IDeliveryNotePlus[]): PendingDerived {
  return useMemo(() => {
    const metaByDn = new Map<string, PendingRowMeta>()
    const countsByDistrict = new Map<string, number>()
    let vipUrgentCount = 0
    let slaRiskCount = 0
    let bigFreightCount = 0
    let customerPickupCount = 0
    const waitHoursList: number[] = []

    for (const dn of rows) {
      const id = dn.id ?? ""
      if (!id) continue

      const district = districtLabel(dn.receiverBranch, dn.receiverWarehouse) ?? "Тодорхойгүй"
      countsByDistrict.set(district, (countsByDistrict.get(district) ?? 0) + 1)

      const totalAmount = dn.totalAmount ?? 0
      const deliveryDate = dn.deliveryDate ?? null
      const hoursUntilDelivery = deliveryDate
        ? dayjs(deliveryDate).diff(dayjs(), "hour")
        : Infinity

      let priority: PendingPriority = "NORMAL"
      if (totalAmount >= VIP_AMOUNT_THRESHOLD) priority = "VIP"
      else if (hoursUntilDelivery < 24) priority = "URGENT"

      if (priority !== "NORMAL") vipUrgentCount++

      const sla = computeSla(dn.deliveryDate, dn.createdAt)
      if (sla.severity === "CRITICAL") slaRiskCount++

      const totalSizeNumeric = parseSize(dn.classification?.totalSizeValue)
      if (totalSizeNumeric >= 10) bigFreightCount++

      if (dn.classification?.deliveryChannel === "CUSTOMER_PICKUP") {
        customerPickupCount++
      }

      if (dn.createdAt) {
        waitHoursList.push(dayjs().diff(dayjs(dn.createdAt), "hour", true))
      }

      metaByDn.set(id, {
        priority,
        slaSeverity: sla.severity,
        totalSizeNumeric,
        district,
      })
    }

    const avgWaitHours = waitHoursList.length
      ? waitHoursList.reduce((s, n) => s + n, 0) / waitHoursList.length
      : 0

    const aiRouteGroups = Array.from(countsByDistrict.entries())
      .filter(([, count]) => count > 0)
      .map(([district, count]) => {
        const totalSize = rows
          .filter((dn) => (districtLabel(dn.receiverBranch, dn.receiverWarehouse) ?? "Тодорхойгүй") === district)
          .reduce((s, dn) => s + parseSize(dn.classification?.totalSizeValue), 0)
        return { district, count, totalSize }
      })
      .sort((a, b) => b.count - a.count)

    return {
      metaByDn,
      countsByDistrict,
      vipUrgentCount,
      slaRiskCount,
      bigFreightCount,
      customerPickupCount,
      avgWaitHours,
      aiRouteGroups,
    }
  }, [rows])
}
